import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/data/data-table'
import { StatusBadge } from '@/components/ui/badge'
import { useState } from 'react'
import { formatPercent, percent } from '@roi365/shared'

export function M365TenantDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string }
  const [usersPage, setUsersPage] = useState(1)
  const [search, setSearch] = useState('')

  const { data: tenant } = useQuery({
    queryKey: ['m365-tenants', id],
    queryFn: () => api.get<{ id: string; display_name: string; domain: string; status: string }>(`/m365-tenants/${id}`),
  })

  const { data: licensesData } = useQuery({
    queryKey: ['m365-tenants', id, 'licenses'],
    queryFn: () =>
      api.get<Array<{
        id: string; sku_part_number: string; display_name: string
        total_units: number; assigned_units: number; available_units: number
        monthly_cost_per_unit: number | null
      }>>(`/m365-tenants/${id}/licenses`),
  })

  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ['m365-tenants', id, 'users', usersPage, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(usersPage), perPage: '25' })
      if (search) params.set('search', search)
      return apiFetchJson<{
        ok: boolean
        data: Array<{
          id: string; display_name: string; user_principal_name: string
          department: string | null; account_enabled: number
          last_sign_in_at: string | null; license_count: number
        }>
        meta: { page: number; perPage: number; total: number; totalPages: number }
      }>(`/m365-tenants/${id}/users?${params}`)
    },
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title={tenant?.display_name ?? 'Cargando...'}
        description={tenant?.domain ?? ''}
      />

      {/* Licenses */}
      <Card>
        <CardHeader>
          <CardTitle>Licencias</CardTitle>
        </CardHeader>
        <DataTable
          columns={[
            { key: 'display_name', header: 'Licencia' },
            { key: 'sku_part_number', header: 'SKU', className: 'hidden md:table-cell' },
            {
              key: 'assigned_units', header: 'Asignadas / Total',
              render: (r) => (
                <span className="tabular-nums">
                  {(r as Record<string, number>).assigned_units} / {(r as Record<string, number>).total_units}
                </span>
              ),
            },
            {
              key: 'usage', header: 'Uso',
              render: (r) => {
                const row = r as Record<string, number>
                const pct = percent(row.assigned_units, row.total_units)
                return (
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-20 rounded-full" style={{ backgroundColor: 'var(--color-gray-200)' }}>
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${Math.min(pct, 100)}%`,
                          backgroundColor: pct > 90 ? 'var(--color-warning)' : 'var(--color-brand-secondary)',
                        }}
                      />
                    </div>
                    <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>
                      {formatPercent(pct)}
                    </span>
                  </div>
                )
              },
            },
            {
              key: 'monthly_cost_per_unit', header: 'Coste/ud',
              render: (r) => {
                const cost = (r as Record<string, number | null>).monthly_cost_per_unit
                return cost ? `${cost.toFixed(2)} EUR` : '—'
              },
            },
          ]}
          data={(licensesData ?? []) as Record<string, unknown>[]}
        />
      </Card>

      {/* Users */}
      <Card>
        <CardHeader>
          <CardTitle>Usuarios</CardTitle>
          <input
            type="text"
            placeholder="Buscar usuario..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setUsersPage(1) }}
            className="rounded-md border px-3 py-1.5 text-sm"
            style={{ borderColor: 'var(--color-border)', width: '250px' }}
          />
        </CardHeader>
        <DataTable
          columns={[
            { key: 'display_name', header: 'Nombre' },
            { key: 'user_principal_name', header: 'UPN', className: 'hidden lg:table-cell' },
            { key: 'department', header: 'Departamento', className: 'hidden md:table-cell' },
            {
              key: 'account_enabled', header: 'Estado',
              render: (r) => <StatusBadge status={(r as Record<string, number>).account_enabled ? 'active' : 'disabled'} />,
            },
            {
              key: 'license_count', header: 'Licencias',
              render: (r) => <span className="tabular-nums">{(r as Record<string, number>).license_count}</span>,
            },
            {
              key: 'last_sign_in_at', header: 'Ultimo acceso',
              render: (r) => {
                const d = (r as Record<string, string | null>).last_sign_in_at
                if (!d) return <span style={{ color: 'var(--color-text-tertiary)' }}>Nunca</span>
                return formatDate(d)
              },
            },
          ]}
          data={(usersResponse?.data ?? []) as Record<string, unknown>[]}
          loading={usersLoading}
          page={usersResponse?.meta?.page ?? 1}
          totalPages={usersResponse?.meta?.totalPages ?? 1}
          total={usersResponse?.meta?.total}
          onPageChange={setUsersPage}
        />
      </Card>
    </div>
  )
}
